import { useState, useRef, useCallback, useEffect, useId } from "react";
import { FiX } from "react-icons/fi";
import { v4 } from "uuid";

import apiBackoffice from "../../../../../services/apiBackoffice";
import { useAuth } from "../../../../../hooks/auth";
import { useToast } from "../../../../../hooks/toast";
import { useAdminApi } from "../../../Admin/hooks/useAdminApi";

import Button from "../../../../../components/Atoms/Button";
import Loading from "../../../../../components/Atoms/Loading";
import {
  Confirm,
  Dropdown,
  Field,
  IconButton,
  StatusBadge,
  TextArea,
  TextInput,
} from "../../../../../components/Admin";

import { SectionsModels } from "../../../../../models/SectionsModels";
import { AdminCourseDetail, CourseImages } from "../../../../../models/CourseModels";

import CoverPicker from "./CoverPicker";
import {
  CourseFormState,
  buildCourseChanges,
  parsePrice,
  parseSectionList,
  toFormState,
} from "./courseForm";
import * as S from "./styles";

interface AddCourseTypes {
  onClose: () => void;
  refreshFeth: () => void;
  /** Com `courseId`, o formulário abre em modo edição desse curso. */
  courseId?: string;
}

const EMPTY_FORM: CourseFormState = {
  title: "",
  description: "",
  price: "",
  sectionId: "",
  status: "Active",
  comingSoon: false,
  priority: "0",
  maxInstallments: "",
  pixPrice: "",
  pixDiscount: "",
  subscription: false,
  subscriptionInstallments: "",
};

// GetCourse (backend) só lista na home os cursos com status "Active".
const STATUS_OPTIONS = [
  { value: "Active", label: "Ativo", hint: "Aparece para o aluno" },
  { value: "Inactive", label: "Inativo", hint: "Oculto da home" },
];

type FieldErrors = Partial<Record<"title" | "price" | "sectionId", string>>;

const AddCourse: React.FC<AddCourseTypes> = ({ onClose, refreshFeth, courseId }) => {
  const isEdit = !!courseId;
  const titleId = useId();

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<CourseFormState>(EMPTY_FORM);
  const [thumb, setThumb] = useState("");
  const [thumbVertical, setThumbVertical] = useState("");
  const [teaser, setTeaser] = useState("");
  const [sections, setSections] = useState<SectionsModels[]>([]);
  const [loadingSections, setLoadingSections] = useState(true);
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [confirmClose, setConfirmClose] = useState(false);

  // Modo edição: o curso como veio do servidor.
  const [original, setOriginal] = useState<AdminCourseDetail | null>(null);
  const [loadingCourse, setLoadingCourse] = useState(isEdit);
  const [courseLoadError, setCourseLoadError] = useState("");
  const initialForm = useRef<CourseFormState>(EMPTY_FORM);

  const { fetchSections, fetchCourseDetail, updateCourse } = useAdminApi();
  const { user } = useAuth();
  const { addToast } = useToast();

  const setField = <K extends keyof CourseFormState>(key: K, value: CourseFormState[K]) => {
    setForm((previous) => ({ ...previous, [key]: value }));
    if (key in fieldErrors) setFieldErrors((previous) => ({ ...previous, [key]: undefined }));
  };

  useEffect(() => {
    let active = true;

    fetchSections()
      .then((result) => {
        if (active) setSections(Array.isArray(result.data) ? result.data : []);
      })
      .catch(() => {
        if (active) {
          addToast({
            type: "error",
            title: "Erro ao carregar seções",
            description: "Não foi possível buscar as seções da escola.",
          });
        }
      })
      .finally(() => {
        if (active) setLoadingSections(false);
      });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCourse = useCallback(async () => {
    if (!courseId) return;
    setLoadingCourse(true);
    setCourseLoadError("");
    try {
      const { data } = await fetchCourseDetail(courseId);
      if (!data || !data.courseid) throw new Error();
      const state = toFormState(data);
      initialForm.current = state;
      setOriginal(data);
      setForm(state);
    } catch (err: any) {
      setCourseLoadError(
        err?.response?.data?.error ||
          "Não foi possível carregar o curso. Verifique a conexão e tente de novo."
      );
    } finally {
      setLoadingCourse(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  useEffect(() => {
    loadCourse();
  }, [loadCourse]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setThumb("");
    setThumbVertical("");
    setTeaser("");
    setFieldErrors({});
    setFormError("");
  };

  // Formulário "sujo": algo difere do ponto de partida (vazio ou o curso
  // carregado) ou uma imagem foi selecionada.
  const isDirty =
    thumb !== "" ||
    thumbVertical !== "" ||
    teaser !== "" ||
    JSON.stringify(form) !== JSON.stringify(isEdit ? initialForm.current : EMPTY_FORM);

  const handleClose = () => {
    if (!isDirty || saving) {
      onClose();
      return;
    }
    setConfirmClose(true);
  };

  // ESC fecha (com a mesma confirmação do X). O Confirm cuida do próprio ESC, e
  // um Dropdown aberto consome o ESC (preventDefault) para fechar só o painel.
  useEffect(() => {
    if (confirmClose) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) handleClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  });

  const discardAndClose = () => {
    setConfirmClose(false);
    resetForm();
    onClose();
  };

  // O erro aparece no rodapé do modal, além do toast.
  const showError = (title: string, description: string) => {
    setFormError(`${title}: ${description}`);
    addToast({ type: "error", title, description });
  };

  const sectionOptions = sections.map((section) => ({
    value: section.sectionid,
    label: section.title,
    hint: section.sectionid,
  }));

  // Curso apontando para uma seção que foi excluída: o valor salvo não está na lista.
  const sectionMissing =
    !loadingSections && form.sectionId !== "" && !sections.some((s) => s.sectionid === form.sectionId);

  const submitEdit = async () => {
    if (!courseId || !original) return;

    const { changes, errors } = buildCourseChanges(
      initialForm.current,
      form,
      parseSectionList(original.sectionid).slice(1)
    );
    if (errors.length) {
      showError("Confira os campos", errors.join(". "));
      return;
    }

    const images: CourseImages = {};
    if (thumb) images.thumburl_base64 = thumb;
    if (thumbVertical) images.thumburlvertical_base64 = thumbVertical;
    if (teaser) images.teaserurl_base64 = teaser;

    if (Object.keys(changes).length === 0 && Object.keys(images).length === 0) {
      addToast({ type: "info", title: "Nenhuma alteração para salvar" });
      return;
    }

    setSaving(true);
    try {
      const response = await updateCourse(courseId, changes, images);
      if (response.data?.message !== "OK") {
        throw new Error(response.data?.error || "O servidor não confirmou a gravação.");
      }

      addToast({ type: "success", title: "Curso atualizado!" });
      onClose();
      refreshFeth();
    } catch (err: any) {
      showError(
        "Erro ao salvar curso",
        err?.response?.data?.error ||
          err?.message ||
          "Ocorreu um erro ao salvar, por favor, tente novamente."
      );
    } finally {
      setSaving(false);
    }
  };

  const submitCreate = async () => {
    const title = form.title.trim();

    // A Lambda AddCourse exige todos estes campos: se faltar um, ela não grava e responde vazio.
    const saveBody = {
      userid: user.userid,
      schoolid: user.schoolid,
      courseid: v4(),
      title,
      description: form.description.trim() || title,
      price: parsePrice(form.price),
      comingsoon: form.comingSoon ? "true" : "false",
      // Obrigatório na Lambda, mas sem uso na home atual: vai vazio (" ") como no resto do sistema.
      levelidlist: " ",
      sectionid: JSON.stringify([form.sectionId]),
      status: form.status,
      thumburl_base64: thumb,
      thumburlvertical_base64: thumbVertical,
      teaserurl_base64: teaser || thumb,
    };

    setSaving(true);
    try {
      const response = await apiBackoffice.post("backoffice/course/add", saveBody);

      if (response.data !== "OK") {
        throw new Error(
          response.data?.errorMessage ||
            response.data?.message ||
            "O servidor não confirmou a gravação. O curso não foi salvo."
        );
      }

      addToast({ type: "success", title: "Curso criado!" });
      resetForm();
      onClose();
      refreshFeth();
    } catch (err: any) {
      showError(
        "Erro ao salvar curso",
        err?.response?.data?.message ||
          err?.message ||
          "Ocorreu um erro ao salvar, por favor, tente novamente."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (saving) return;

    setFormError("");

    const errors: FieldErrors = {};
    if (form.title.trim().length < 3) errors.title = "Use ao menos 3 caracteres.";
    const price = parsePrice(form.price);
    if (form.price.trim() === "" || isNaN(price) || price < 0) {
      errors.price = "Informe um valor, ex.: 197,00.";
    }
    setFieldErrors(errors);
    if (errors.title || errors.price) {
      showError("Confira os campos", "Os campos destacados precisam de ajuste.");
      // Leva o teclado (e o leitor de tela) direto ao primeiro campo inválido.
      requestAnimationFrame(() =>
        document.querySelector<HTMLElement>('[role="dialog"] [aria-invalid="true"]')?.focus()
      );
      return;
    }

    // Na edição as capas já existem; só são enviadas se trocadas.
    if (!isEdit && (thumb === "" || thumbVertical === "")) {
      showError("Imagens obrigatórias", "Selecione a capa horizontal e a capa vertical.");
      return;
    }

    if (form.sectionId === "" || sectionMissing) {
      setFieldErrors({ sectionId: "Escolha a seção em que o curso aparece." });
      showError("Seção obrigatória", "Selecione a seção em que o curso aparece na home.");
      return;
    }

    if (isEdit) await submitEdit();
    else await submitCreate();
  };

  // No carrinho, o preço PIX (quando preenchido) é o valor cobrado — ver hooks/cart.tsx.
  const pixPriceStale =
    isEdit &&
    form.price !== initialForm.current.price &&
    form.pixPrice !== "" &&
    form.pixPrice === initialForm.current.pixPrice;

  const heading = isEdit ? "Editar curso" : "Novo curso";

  const closeButton = (
    <IconButton label="Fechar" onClick={isEdit && (loadingCourse || courseLoadError) ? onClose : handleClose}>
      <FiX size={18} />
    </IconButton>
  );

  if (isEdit && (loadingCourse || courseLoadError)) {
    return (
      <S.Dialog role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <S.Header>
          <S.HeaderText>
            <S.Title id={titleId}>{heading}</S.Title>
          </S.HeaderText>
          {closeButton}
        </S.Header>
        <S.Centered>
          {loadingCourse ? (
            <Loading />
          ) : (
            <>
              <S.Callout tone="error" role="alert">
                {courseLoadError}
              </S.Callout>
              <Button type="button" onClick={loadCourse}>
                Tentar novamente
              </Button>
            </>
          )}
        </S.Centered>
      </S.Dialog>
    );
  }

  return (
    <S.Dialog
      as="form"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onSubmit={handleSubmit}
      noValidate
    >
      <S.Header>
        <S.HeaderText>
          <S.Title id={titleId}>{heading}</S.Title>
          {isEdit && original ? (
            <S.Meta>
              <StatusBadge
                label={initialForm.current.status === "Inactive" ? "Inativo" : "Ativo"}
                tone={initialForm.current.status === "Inactive" ? "neutral" : "positive"}
              />
              <code>{original.courseid}</code>
            </S.Meta>
          ) : (
            <S.Meta>Preencha as informações e as capas. Pagamento é configurado depois, na edição.</S.Meta>
          )}
        </S.HeaderText>
        {closeButton}
      </S.Header>

      <S.Body>
        <S.Covers aria-label="Capas do curso">
          <CoverPicker
            label="Capa horizontal"
            shape="horizontal"
            required={!isEdit}
            currentUrl={original?.thumburl}
            value={thumb}
            onChange={setThumb}
          />
          <CoverPicker
            label="Capa vertical"
            shape="vertical"
            required={!isEdit}
            currentUrl={original?.thumburlvertical}
            value={thumbVertical}
            onChange={setThumbVertical}
          />
          <CoverPicker
            label="Destaque"
            shape="horizontal"
            currentUrl={original?.teaserurl}
            value={teaser}
            onChange={setTeaser}
          />
        </S.Covers>

        <S.Sections>
          <S.Section aria-labelledby={`${titleId}-info`}>
            <S.SectionTitle id={`${titleId}-info`}>Informações</S.SectionTitle>
            <S.Grid>
              <Field label="Título" required full error={fieldErrors.title}>
                <TextInput
                  value={form.title}
                  onChange={(e) => setField("title", e.target.value)}
                  placeholder="Ex.: Aconselhamento Bíblico Avançado"
                />
              </Field>
              <Field label="Descrição" full hint="Se ficar vazia, usa o título.">
                <TextArea
                  value={form.description}
                  onChange={(e) => setField("description", e.target.value)}
                  rows={3}
                />
              </Field>
              <S.DropdownSlot>
                <Dropdown
                  label="Seção na home"
                  placeholder={loadingSections ? "Carregando seções…" : "Selecione a seção"}
                  options={sectionOptions}
                  value={sectionMissing ? "" : form.sectionId}
                  onChange={(value) => setField("sectionId", value)}
                  disabled={loadingSections}
                  emptyMessage="Nenhuma seção cadastrada"
                />
                {sectionMissing ? (
                  <S.SlotHint tone="error" role="alert">
                    A seção salva neste curso não existe mais. Escolha uma das atuais.
                  </S.SlotHint>
                ) : fieldErrors.sectionId ? (
                  <S.SlotHint tone="error">{fieldErrors.sectionId}</S.SlotHint>
                ) : (
                  <S.SlotHint>Obrigatória.</S.SlotHint>
                )}
              </S.DropdownSlot>
              <S.DropdownSlot>
                <Dropdown
                  label="Status"
                  options={STATUS_OPTIONS}
                  value={form.status}
                  onChange={(value) => setField("status", value as CourseFormState["status"])}
                />
              </S.DropdownSlot>
              {isEdit && (
                <Field label="Prioridade" hint="Ordem do curso na fileira; 0 vem primeiro.">
                  <TextInput
                    inputMode="numeric"
                    value={form.priority}
                    onChange={(e) => setField("priority", e.target.value)}
                  />
                </Field>
              )}
              <S.Switch>
                <input
                  type="checkbox"
                  role="switch"
                  checked={form.comingSoon}
                  onChange={(e) => setField("comingSoon", e.target.checked)}
                />
                <span>
                  Em breve
                  <small>O card mostra "Em breve" no lugar do preço.</small>
                </span>
              </S.Switch>
            </S.Grid>
          </S.Section>

          <S.Section aria-labelledby={`${titleId}-pay`}>
            <S.SectionTitle id={`${titleId}-pay`}>Preço e pagamento</S.SectionTitle>
            {isEdit && (
              <S.SectionNote>
                No carrinho, o valor cobrado é o preço PIX quando ele está preenchido. Campo vazio
                remove a configuração.
              </S.SectionNote>
            )}
            <S.Grid>
              <Field label="Preço" required prefix="R$" error={fieldErrors.price}>
                <TextInput
                  inputMode="decimal"
                  value={form.price}
                  onChange={(e) => setField("price", e.target.value)}
                  placeholder="197,00"
                />
              </Field>
              {isEdit && (
                <>
                  <Field label="Parcelas no cartão" suffix="x" hint="De 1 a 24.">
                    <TextInput
                      inputMode="numeric"
                      value={form.maxInstallments}
                      onChange={(e) => setField("maxInstallments", e.target.value)}
                      placeholder="4"
                    />
                  </Field>
                  <Field label="Preço no PIX" prefix="R$">
                    <TextInput
                      inputMode="decimal"
                      value={form.pixPrice}
                      onChange={(e) => setField("pixPrice", e.target.value)}
                      placeholder="180,00"
                    />
                  </Field>
                  <Field label="Desconto no PIX" suffix="%">
                    <TextInput
                      inputMode="decimal"
                      value={form.pixDiscount}
                      onChange={(e) => setField("pixDiscount", e.target.value)}
                      placeholder="8"
                    />
                  </Field>
                  <S.Switch>
                    <input
                      type="checkbox"
                      role="switch"
                      checked={form.subscription}
                      onChange={(e) => setField("subscription", e.target.checked)}
                    />
                    <span>
                      Cobrar como assinatura
                      <small>Usa as parcelas da assinatura no lugar das do cartão.</small>
                    </span>
                  </S.Switch>
                  {form.subscription && (
                    <Field label="Parcelas da assinatura" suffix="x" hint="De 1 a 24.">
                      <TextInput
                        inputMode="numeric"
                        value={form.subscriptionInstallments}
                        onChange={(e) => setField("subscriptionInstallments", e.target.value)}
                        placeholder="12"
                      />
                    </Field>
                  )}
                </>
              )}
            </S.Grid>
            {pixPriceStale && (
              <S.Callout tone="warn">
                O preço mudou, mas o preço PIX continua R$ {form.pixPrice}. No carrinho, é ele que é
                cobrado.
              </S.Callout>
            )}
          </S.Section>
        </S.Sections>
      </S.Body>

      <S.Footer>
        {formError ? (
          <S.Callout tone="error" role="alert">
            {formError}
          </S.Callout>
        ) : (
          <S.FooterStatus dirty={isDirty} aria-live="polite">
            {isDirty ? "Alterações não salvas" : isEdit ? "Nenhuma alteração" : "Curso novo"}
          </S.FooterStatus>
        )}
        <S.FooterActions>
          <Button type="button" contrast onClick={handleClose} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" loading={saving}>
            {isEdit ? "Salvar alterações" : "Criar curso"}
          </Button>
        </S.FooterActions>
      </S.Footer>

      <Confirm
        open={confirmClose}
        title="Descartar alterações?"
        description="Há alterações não salvas no formulário. Ao fechar, elas serão perdidas."
        confirmLabel="Descartar"
        danger
        onConfirm={discardAndClose}
        onCancel={() => setConfirmClose(false)}
      />
    </S.Dialog>
  );
};

export default AddCourse;
